import {
  useCallback,
  useEffect,
  useState,
} from 'react'

import {
  ApiError,
} from '../api/client'

import {
  getHeader,
} from '../api/header'

import {
  getMe,
} from '../api/me'

import {
  ACCOUNT_REFRESH_EVENT,
} from '../events/account'

import type {
  HeaderResponse,
} from '../types/header'

import type {
  MeResponse,
} from '../types/me'

export type AuthStatus =
  | 'checking'
  | 'authenticated'
  | 'guest'
  | 'error'

interface UseHeaderResult {
  status: AuthStatus
  me: MeResponse | null
  header: HeaderResponse | null
  error: string | null
  reload: () => Promise<void>
}

export function useHeader(): UseHeaderResult {
  const [status, setStatus] =
    useState<AuthStatus>('checking')

  const [me, setMe] =
    useState<MeResponse | null>(null)

  const [header, setHeader] =
    useState<HeaderResponse | null>(null)

  const [error, setError] =
    useState<string | null>(null)

  const load = useCallback(
    async (
      showChecking = true,
    ) => {
      if (showChecking) {
        setStatus('checking')
      }

      setError(null)

      try {
        const meResponse =
          await getMe()

        const headerResponse =
          await getHeader()

        setMe(meResponse)
        setHeader(headerResponse)

        setStatus('authenticated')
      } catch (requestError) {
        if (
          requestError instanceof ApiError &&
          requestError.status === 401
        ) {
          setMe(null)
          setHeader(null)
          setError(null)
          setStatus('guest')

          return
        }

        console.error(
          'Failed to initialize Kraken header:',
          requestError,
        )

        /*
         * При фоновом обновлении сохраняем
         * уже отрисованный Header. Ошибка
         * синхронизации не должна мигать
         * HeaderSkeleton на весь экран.
         */
        if (!showChecking) {
          return
        }

        setMe(null)
        setHeader(null)

        setError(
          requestError instanceof Error
            ? requestError.message
            : (
                'Не удалось загрузить '
                + 'данные Академии.'
              ),
        )

        setStatus('error')
      }
    },
    [],
  )

  useEffect(() => {
    void load()
  }, [
    load,
  ])

  useEffect(() => {
    const handleAccountRefresh =
      () => {
        void load(false)
      }

    window.addEventListener(
      ACCOUNT_REFRESH_EVENT,
      handleAccountRefresh,
    )

    return () => {
      window.removeEventListener(
        ACCOUNT_REFRESH_EVENT,
        handleAccountRefresh,
      )
    }
  }, [
    load,
  ])

  return {
    status,
    me,
    header,
    error,
    reload: () => load(false),
  }
}

